import { DialogTitle } from "@radix-ui/react-dialog";
import { memo, useCallback, useState } from "react";
import { z } from "zod";

import type { EcReceiptEmailResendInput } from "@/lib/gql/__generated__/graphql";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent, DialogHeader, DialogTrigger } from "@/components/ui/dialog/dialog";
import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";

import { ManagementECReceiptDetailVoucherIssuedSectionLabelAndTitleItem as LabelAndTitleItem } from "../_shared/label-and-title-item";

import type { ViewData } from "../../../../model/type";

type Props = {
	ecReceiptItem: ViewData["ecReceiptItem"];
	onResendECReceiptEmail: (input: EcReceiptEmailResendInput) => Promise<void>;
};

export const ManagementECReceiptDetailBaseInfoSection = memo(({ ecReceiptItem, onResendECReceiptEmail }: Props) => {
	const [open, setOpen] = useState(false);
	const activateModal = useCallback(() => setOpen(true), []);
	const deactivateModal = useCallback(() => setOpen(false), []);
	const handleOpenChange = useCallback((input: boolean) => {
		setOpen(input);
	}, []);

	const [email, setEmail] = useState<{
		value: string;
		error: string | undefined;
	}>({
		value: ecReceiptItem.guestEmail,
		error: undefined,
	});

	const handleChangeEmail = useCallback((email: string) => {
		const emailZodResponse = z.string().email().safeParse(email);

		setEmail({
			value: email,
			error: emailZodResponse.success ? undefined : emailZodResponse.error.formErrors.formErrors[0],
		});
	}, []);

	const disabled = !!email.error;

	const handleClickResendECReceiptEmailButton = useCallback(async () => {
		if (disabled) return;

		deactivateModal();

		await onResendECReceiptEmail({
			hotelID: ecReceiptItem.hotelID,
			receiptID: ecReceiptItem.receiptID,
			guestEmail: email.value,
		});
	}, [
		deactivateModal,
		disabled,
		ecReceiptItem.hotelID,
		ecReceiptItem.receiptID,
		email.value,
		onResendECReceiptEmail,
	]);

	return (
		<div className="space-y-4">
			<h4>基本情報</h4>
			<div className="space-y-6 rounded border border-zinc-200 p-4">
				<div className="flex gap-x-12">
					<LabelAndTitleItem label="購入日" value={ecReceiptItem.purchasedDate} />
					<LabelAndTitleItem label="レシートID" value={ecReceiptItem.receiptID} />
				</div>
				<div className="space-y-6">
					<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
						<LabelAndTitleItem
							label="購入者名"
							value={
								<>
									{ecReceiptItem.guestNameKana && (
										<ruby className="text-xs text-zinc-400">{ecReceiptItem.guestNameKana}</ruby>
									)}
									<div className="flex items-end gap-x-4 text-xl font-semibold">
										<p>{ecReceiptItem.guestName}</p>
										<p className="text-sm">様</p>
									</div>
								</>
							}
						/>
						<LabelAndTitleItem
							label="合計金額"
							value={
								<p className="text-xl font-semibold">
									{ecReceiptItem.totalGuestCharge.toLocaleString()}円
								</p>
							}
						/>
					</div>

					<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
						<div className="col-span-1 space-y-4">
							<div className="space-y-2">
								<LabelAndTitleItem label="メールアドレス" value={ecReceiptItem.guestEmail} />

								<Dialog open={open} onOpenChange={handleOpenChange}>
									<DialogTrigger asChild>
										<Button
											size="sm"
											variant={"outline"}
											onClick={activateModal}
											className="text-xs print:hidden"
										>
											メールの再送・メールアドレスの更新
										</Button>
									</DialogTrigger>

									<DialogContent>
										<DialogHeader>
											<DialogTitle>メールの再送・メールアドレスの更新</DialogTitle>
										</DialogHeader>

										<div className="space-y-8">
											<InputTextField
												value={email.value}
												errorMessage={email.error}
												handleChange={handleChangeEmail}
											/>
											<Button
												variant={disabled ? "disabled" : "default"}
												onClick={handleClickResendECReceiptEmailButton}
											>
												メールの再送・メールアドレスの更新
											</Button>
										</div>
									</DialogContent>
								</Dialog>
							</div>
							<LabelAndTitleItem label="電話番号" value={ecReceiptItem.guestTel} />
							<LabelAndTitleItem
								label="住所"
								value={
									<>
										<p className="text-sm">{ecReceiptItem.guestPostalCode}</p>
										<p>{ecReceiptItem.guestAddress}</p>
									</>
								}
							/>
						</div>
						<LabelAndTitleItem
							label="宿泊券情報"
							value={
								<div className="col-span-1">
									<div className="space-y-4">
										{ecReceiptItem.purchasedItems.map((item, index) => (
											<div key={index}>
												<p>
													{item.name}（{item.amount}個）
												</p>
												<p>{item.eachPrice.toLocaleString()}円</p>
											</div>
										))}
									</div>
								</div>
							}
						/>
					</div>
				</div>
			</div>
		</div>
	);
});

ManagementECReceiptDetailBaseInfoSection.displayName = "ManagementECReceiptDetailBaseInfoSection";
